import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { stripExtraTags } from "@common/domain/tag-filter";
import ChatSessionsContext from "./ChatSessionsContext";
import { Outlet } from "react-router-dom";
import { ChatSession, Message } from "../interfaces/Chat";
import { useChat } from "../hooks/UseChat";
import { buildChatSession, upsertChatSession } from "../helpers/chatSessionBuilders";
import { Persona, getPersonas } from "../api/Persona";
import { useUserInfoContext } from "./UserInfoContext";
import { config } from "@config";
import {
  trackChatSessionEnd,
  trackChatSessionStart,
  beginChatWallClock,
} from "../analytics/chatAnalytics";
import { setActiveConversationContext } from "../api/ErrorReporter";

export function ChatSessionsContextProvider() {
  const { userInfo } = useUserInfoContext();
  const [persona, setPersona] = useState<Persona | undefined>(undefined);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [selectedChatSession, setSelectedChatSession] = useState<
    string | null
  >();
  const [allMessages, setAllMessages] = useState<Record<string, Message[]>>({});
  const messages = useMemo(() => (selectedChatSession ? allMessages[selectedChatSession] || [] : []), [allMessages, selectedChatSession]);

  useEffect(() => {
    setActiveConversationContext({ chatId: selectedChatSession || undefined });
  }, [selectedChatSession]);

  const setMessages = useCallback((
    newMessages: Message[] | ((prev: Message[]) => Message[]),
    chatId?: string
  ) => {
    const targetChatId = chatId || selectedChatSession;
    if (!targetChatId) return;

    setAllMessages((prev) => {
      const currentMessages = prev[targetChatId] || [];
      const updatedMessages = typeof newMessages === 'function' ? newMessages(currentMessages) : newMessages;
      return {
        ...prev,
        [targetChatId]: updatedMessages,
      };
    });
  }, [selectedChatSession]);

  const noMsgReset = useRef(false);
  const [filterFromYear, setFilterFromYear] = useState<number | null>(null);
  const [filterToYear, setFilterToYear] = useState<number | null>(null);

  const selectedChatSessionRef = useRef(selectedChatSession);
  selectedChatSessionRef.current = selectedChatSession;

  const { createNewChatSession, getHistory, archiveChatSession } = useChat();
  const historyLoaded = useRef(false);

  useEffect(() => {
    const onPageHide = () => {
      trackChatSessionEnd(selectedChatSessionRef.current ?? undefined);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  useEffect(() => {
    if (userInfo && !historyLoaded.current) {
        getHistory(userInfo).then(async (res) => {
            if (res) {
                let sessions = res;
                if (config.companyName !== "Gargantua") {
                    try {
                        const fetched = await getPersonas(userInfo);
                        const filtered = fetched.filter((p) => p.category === config.companyName);
                        sessions = res.filter((session) =>
                            filtered.some((p) => p.id === session.chatInfo.personaId)
                        );
                        setPersona((currentPersona) => currentPersona ?? filtered[0]);
                    } catch (e) {
                        console.error(`Failed to filter ${config.companyName} history`, e);
                        sessions = []; // Fail closed to prevent exposing other personas' chats
                    }
                }
                setChatSessions(sessions);
                historyLoaded.current = true;
            }
        }).catch(err => {
            console.error("Failed to load history", err);
        });
    }
  }, [userInfo, getHistory]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrawerClose = useCallback(() => {
    setIsClosing(true);
    setMobileOpen(false);
  }, []);

  const handleDrawerTransitionEnd = useCallback(() => {
    setIsClosing(false);
  }, []);

  const handleDrawerToggle = useCallback(() => {
    if (!isClosing) {
      setMobileOpen((prev) => !prev);
    }
  }, [isClosing]);

  const updateSelectedChatSession = useCallback((chatSessionId: string) => {
    if (chatSessionId === selectedChatSession) return;
    if (selectedChatSession) {
      trackChatSessionEnd(selectedChatSession);
    }
    setSelectedChatSession(chatSessionId);
    beginChatWallClock(chatSessionId, 'text');
  }, [selectedChatSession]);

  const promoteChatSession = useCallback((chatId: string, personaId?: string) => {
    setChatSessions((prev) => upsertChatSession(prev, buildChatSession(undefined, personaId, chatId)));
    if (chatId === selectedChatSession) return;
    if (selectedChatSession) {
      trackChatSessionEnd(selectedChatSession);
    }
    setSelectedChatSession(chatId);
    beginChatWallClock(chatId, 'text');
  }, [selectedChatSession]);

  const addNewChatSession = useCallback((question?: string) => {
    if (selectedChatSession) {
      trackChatSessionEnd(selectedChatSession);
    }
    const newChatSession = createNewChatSession(question, persona?.id);

    setChatSessions((prevChatSessions) => [
      newChatSession,
      ...prevChatSessions,
    ]);

    if (!noMsgReset.current) setMessages([], newChatSession.chatInfo.chatId);
    const id = newChatSession.chatInfo.chatId;
    setSelectedChatSession(id);
    trackChatSessionStart(id, 'text');
    return id;
  }, [createNewChatSession, persona, selectedChatSession, setMessages]);

  const updateNewChatSession = useCallback((question: string) => {
    // update the chat session title if it is a new chat session
    setChatSessions((prevChatSessions) => {
      const updatedChatSessions = [...prevChatSessions];
      if (updatedChatSessions.length > 0) {
        updatedChatSessions[0] = {
          ...updatedChatSessions[0],
          chatInfo: {
            ...updatedChatSessions[0].chatInfo,
            chatTitle: stripExtraTags(question),
          },
        };
      }
      return updatedChatSessions;
    });
  }, []);

  const resetSelectedChatSession = useCallback(() => {
    if (selectedChatSession) {
      trackChatSessionEnd(selectedChatSession);
    }
    setSelectedChatSession(null);
  }, [selectedChatSession]);

  const hideChatSession = useCallback((chatId: string) => {
    // Remove from UI immediately
    setChatSessions((prev: ChatSession[]) => prev.filter(s => s.chatInfo.chatId !== chatId));
    if (selectedChatSession === chatId) {
      trackChatSessionEnd(chatId);
      setSelectedChatSession(null);
    }
    // Archive in backend (fire-and-forget, data is preserved)
    if (userInfo) {
      archiveChatSession(userInfo, chatId).catch(err =>
        console.error('Failed to archive chat session:', err)
      );
    }
  }, [selectedChatSession, userInfo, archiveChatSession]);

  const updatePersona = useCallback((persona: Persona | undefined) => {
    if (selectedChatSession) {
      trackChatSessionEnd(selectedChatSession);
    }
    setPersona(persona);
    setSelectedChatSession(null);
  }, [selectedChatSession]);

  const contextValue = useMemo(() => ({
    chatSessions,
    updateSelectedChatSession,
    addNewChatSession,
    promoteChatSession,
    updateNewChatSession,
    resetSelectedChatSession,
    hideChatSession,
    setChatSessions,
    selectedChatSession,
    messages,
    allMessages,
    setMessages,
    noMsgReset,
    filterFromYear,
    setFilterToYear,
    filterToYear,
    setFilterFromYear,
    mobileOpen,
    handleDrawerToggle,
    handleDrawerTransitionEnd,
    handleDrawerClose,
    inputRef,
    persona,
    updatePersona
  }), [
    chatSessions,
    updateSelectedChatSession,
    addNewChatSession,
    promoteChatSession,
    updateNewChatSession,
    resetSelectedChatSession,
    hideChatSession,
    selectedChatSession,
    messages,
    allMessages,
    setMessages,
    filterFromYear,
    filterToYear,
    mobileOpen,
    handleDrawerToggle,
    handleDrawerTransitionEnd,
    handleDrawerClose,
    persona,
    updatePersona
  ]);

  return (
    <ChatSessionsContext.Provider value={contextValue}>
      <Outlet />
    </ChatSessionsContext.Provider>
  );
}
